import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import type {Subject} from '@github-ui/comment-box/subject'
import {validateIssueBody} from '@github-ui/entity-validators'
import {IssueBodyEditor} from '@github-ui/issue-body/components/IssueBodyEditor'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {clsx} from 'clsx'
import {useMemo, useState, type RefObject} from 'react'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useShouldShowCopilotFunctionalityInIssues} from '@github-ui/use-should-show-copilot-functionality-in-issues'
import {Banner} from '@primer/react'
import {Stack} from '@primer/react/experimental'
import {announce} from '@github-ui/aria-live'

import {useUpdateDescriptionMutation} from '../../mutations/use-update-description-mutation'
import styles from './DescriptionEditor.module.css'
import {
  useLayoutBaseRepository,
  useLayoutPullRequestId,
  useLayoutPullRequestNumber,
  useLayoutPullRequestTitle,
} from '../../stores/selectors'
import {useReturnFocus} from '@github-ui/use-return-focus'

// eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- static string literal is safe
const PLACEHOLDER_TEXT = 'No description provided.' as SafeHTMLString
const DESCRIPTION_SAVE_ERROR = 'Failed to update the description.'
const DESCRIPTION_STALE_ERROR =
  'The description was updated by someone else. Cancel to see the latest version, then reopen to continue editing.'

/** Session storage key for persisting an unsaved PR description draft. */
const prDescriptionDraftKey = (pullRequestId: number) => `pr-description-draft-${pullRequestId}`

type DescriptionEditorProps = {
  /** Current description as sanitized HTML, or null/undefined if none. Used by the read-only viewer. */
  bodyHtml: SafeHTMLString | null | undefined
  /** Current description as raw markdown. Used as the initial draft when editing. */
  body: string
  isEditing: boolean
  onEditingChange: (isEditing: boolean) => void
  /** Class for the viewer container; ignored when editing. */
  viewerClassName?: string
  /** Element to receive focus when the editor is dismissed (e.g. the edit trigger). */
  returnFocusRef?: RefObject<HTMLElement | null>
}

/**
 * Shared description display + editing experience for pull requests. Handles
 * toggling between a `NewMarkdownViewer` and a `CommentBox` editor, draft
 * persistence, stale-content detection, and saving via `useUpdateDescriptionMutation`.
 *
 * The parent owns the `isEditing` state so it can place the edit trigger
 * wherever it makes sense (e.g. an overview kebab menu, a panel title bar).
 * While editing, the trigger should remain mounted but marked `inactive`.
 */
export function DescriptionEditor({
  bodyHtml,
  body,
  isEditing,
  onEditingChange,
  viewerClassName,
  returnFocusRef,
}: DescriptionEditorProps) {
  const pullRequestNumber = useLayoutPullRequestNumber()
  const pullRequestId = useLayoutPullRequestId()
  const pullRequestTitle = useLayoutPullRequestTitle()
  const baseRepository = useLayoutBaseRepository()

  const currentBodyHtml = bodyHtml ?? ''
  const isEmptyDescription = currentBodyHtml === ''

  const [sessionDraft, setSessionDraft, resetSessionDraft] = useSessionStorage<string | undefined>(
    prDescriptionDraftKey(pullRequestId),
    undefined,
  )
  const [draft, setDraft] = useState(() => sessionDraft ?? body)
  const [bodyIsStale, setBodyIsStale] = useState(false)
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | undefined>(undefined)

  const [prevBody, setPrevBody] = useState(body)
  if (body !== prevBody) {
    setPrevBody(body)
    if (isEditing) {
      // The description changed underneath us while editing, so the draft is now stale.
      setBodyIsStale(true)
      setSaveErrorMessage(DESCRIPTION_STALE_ERROR)
    } else {
      setDraft(sessionDraft ?? body)
    }
  }

  const [prevIsEditing, setPrevIsEditing] = useState(isEditing)
  if (isEditing !== prevIsEditing) {
    setPrevIsEditing(isEditing)
    if (isEditing) {
      setBodyIsStale(false)
      setDraft(sessionDraft ?? body)
    }
  }

  const repoVisibility = baseRepository.public ? 'PUBLIC' : 'PRIVATE'
  const shouldShowCopilot = useShouldShowCopilotFunctionalityInIssues({
    repositoryVisibility: repoVisibility,
    userCanPushToRepo: baseRepository.currentUserCanPush ?? false,
  })

  const handleDraftChange = (value: string) => {
    setDraft(value)
    setSessionDraft(value)
  }

  const closeEditor = () => {
    onEditingChange(false)
    setSaveErrorMessage(undefined)
    setBodyIsStale(false)
    if (returnFocusRef) {
      requestAnimationFrame(() => returnFocusRef.current?.focus())
    }
  }

  const subject = useMemo<Subject>(
    () => ({
      type: 'pull_request',
      id: {id: pullRequestId.toString(), databaseId: pullRequestId},
      pullRequestNumber,
      repository: {
        databaseId: baseRepository.id,
        nwo: `${baseRepository.ownerLogin}/${baseRepository.name}`,
        slashCommandsEnabled: false,
      },
    }),
    [pullRequestId, pullRequestNumber, baseRepository],
  )

  const validationResult = useMemo(() => validateIssueBody(draft), [draft])

  const {mutateAsync: updateDescription, isPending} = useUpdateDescriptionMutation(error => {
    if (error.cause === 409) {
      setBodyIsStale(true)
      setSaveErrorMessage(DESCRIPTION_STALE_ERROR)
    } else {
      setSaveErrorMessage(DESCRIPTION_SAVE_ERROR)
    }
  })

  const handleSave = async () => {
    if (!validationResult.isValid) return
    setSaveErrorMessage(undefined)
    try {
      await updateDescription({body: draft})
      resetSessionDraft()
      announce('Pull request description updated successfully.')
      closeEditor()
    } catch {
      // Error already handled by the onError callback in useUpdateDescriptionMutation
    }
  }

  const onCancel = () => {
    // Preserve the draft when stale so users can recover their edits after seeing the latest version.
    if (!bodyIsStale) {
      resetSessionDraft()
    }
    setDraft(body)
    closeEditor()
  }

  // This is intentionally a separate mutation instance from the main edit functionality, so that it doesn't share in
  // the optimistic update logic (which looks janky when interacting with checkboxes because the client side Markdown
  // renderer md2html doesn't support checkboxes).
  const directEditMutation = useUpdateDescriptionMutation()

  /** Called when Markdown is updated without entering edit mode (ie, checkbox interaction). */
  const handleMarkdownChange = async (newValue: string) => {
    await directEditMutation.mutateAsync({body: newValue})
  }

  const {returnFocusRef: errorBannerReturnFocusRef, containerRef: errorBannerRef} = useReturnFocus()

  if (isEditing) {
    return (
      <Stack direction="vertical" gap="condensed">
        <IssueBodyEditor
          editorDisabled={isPending}
          subjectId={pullRequestId.toString()}
          subject={subject}
          body={draft}
          bodyIsStale={bodyIsStale}
          onChange={handleDraftChange}
          onCancel={onCancel}
          onCommit={handleSave}
          repoOwner={baseRepository.ownerLogin}
          repoName={baseRepository.name}
          title={pullRequestTitle}
          shouldShowCopilotFunctionality={shouldShowCopilot}
          trailingIcon={false}
          ref={errorBannerReturnFocusRef}
        />
        {saveErrorMessage && (
          <Banner
            role="alert"
            variant="critical"
            title={saveErrorMessage}
            onDismiss={bodyIsStale ? undefined : () => setSaveErrorMessage(undefined)}
            ref={errorBannerRef}
          />
        )}
      </Stack>
    )
  }

  return (
    <div className={clsx(viewerClassName, directEditMutation.isPending && styles.pendingBody)}>
      {isEmptyDescription ? (
        <NewMarkdownViewer verifiedHTML={PLACEHOLDER_TEXT} markdownValue={body} className="fgColor-muted text-italic" />
      ) : (
        <Stack direction="vertical" gap="condensed" tabIndex={-1} ref={errorBannerReturnFocusRef}>
          <NewMarkdownViewer
            verifiedHTML={currentBodyHtml}
            markdownValue={body}
            disabled={directEditMutation.isPending}
            onChange={handleMarkdownChange}
          />
          {directEditMutation.error && (
            // The error banner can appear in read mode when markdown updates (checkboxes) fail to save
            <Banner
              role="alert"
              variant="critical"
              title={DESCRIPTION_SAVE_ERROR}
              onDismiss={directEditMutation.reset}
              ref={errorBannerRef}
            >
              {directEditMutation.error.message}
            </Banner>
          )}
        </Stack>
      )}
    </div>
  )
}
